/**
 * Kanzlei-Sicherheits-Panel (Go-Live S1) — Passwort ändern + 2FA (TOTP) verwalten.
 * Modal-Overlay im lex-Design, genutzt im AdvisorLexCockpit (Kanzlei-Portal).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { X, ShieldCheck, KeyRound, Copy, Users, Check as CheckIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Team & Datenzugriff (Sep 2026): Transparenz-Liste aller Kanzlei-Logins mit
// Vier-Augen-Freischaltung — niemand kann den eigenen Zugriff ändern.
const TeamAccessSection = () => {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => axios.get(`${API}/lex/advisor/team-access`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setAccess = async (acc, access) => {
    setBusyId(acc.id);
    try {
      await axios.post(`${API}/lex/advisor/team-access/${acc.id}`, { access });
      toast.success(access
        ? `Mandantendaten-Zugriff für ${acc.name || acc.email} erteilt.`
        : `Zugriff für ${acc.name || acc.email} entzogen — Daten werden maskiert.`);
      load();
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Änderung fehlgeschlagen.');
    } finally { setBusyId(null); }
  };

  if (!data) return null;
  return (
    <div className="space-y-3" data-testid="team-access-section">
      <h3 className="lex-serif text-base font-semibold flex items-center gap-2" style={{ color: '#0A1128' }}>
        <Users className="h-4 w-4" /> Team & Datenzugriff
      </h3>
      <p className="text-xs text-slate-500">
        Transparenz für die Mandanten: alle Kanzlei-Zugänge und wer Mandantendaten sehen darf.
        Zugriff wird nach dem <span className="font-semibold">Vier-Augen-Prinzip</span> vergeben —
        niemand kann den eigenen Zugriff ändern. Ohne Zugriff werden alle Anwaltsdaten maskiert (*****).
      </p>
      <div className="border border-slate-200 divide-y divide-slate-100">
        {data.accounts.map((a) => (
          <div key={a.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`team-access-row-${a.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">
                {a.name || '—'}
                {a.primary && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-slate-300 text-slate-500">Hauptkonto</span>}
                {a.is_me && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5" style={{ background: '#FBF7EF', color: '#92650B', border: '1px solid #C5A059' }}>Sie</span>}
                {a.disabled && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-rose-50 text-rose-600 border border-rose-200">deaktiviert</span>}
                {a.setup_pending && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-50 text-slate-500 border border-slate-200">Einrichtung offen</span>}
              </p>
              <p className="lex-mono text-[11px] text-slate-400 truncate">{a.email}</p>
            </div>
            <span className={`text-sm font-bold ${a.data_read_access ? 'text-emerald-600' : 'text-rose-500'}`}
              title={a.data_read_access ? 'Hat Mandantendaten-Zugriff' : 'Kein Zugriff — sieht Daten nur maskiert'}
              data-testid={`team-access-status-${a.id}`}>
              {a.data_read_access ? <CheckIcon className="h-4 w-4 inline" /> : <X className="h-4 w-4 inline" />}
            </span>
            <button onClick={() => setAccess(a, !a.data_read_access)}
              disabled={a.is_me || busyId === a.id}
              title={a.is_me ? 'Vier-Augen-Prinzip: eigener Zugriff nicht änderbar' : ''}
              className="text-[11px] uppercase tracking-wider font-semibold px-2 py-1 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={a.data_read_access
                ? { borderColor: '#FCA5A5', color: '#B91C1C', background: '#FEF2F2' }
                : { borderColor: '#6EE7B7', color: '#047857', background: '#ECFDF5' }}
              data-testid={`team-access-toggle-${a.id}`}>
              {a.data_read_access ? 'Entziehen' : 'Erteilen'}
            </button>
          </div>
        ))}
      </div>
      {(data.log || []).length > 0 && (
        <div data-testid="team-access-log">
          <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-1">Protokoll</p>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {data.log.map((l) => (
              <p key={l.id} className="lex-mono text-[11px] text-slate-500">
                {new Date(l.at).toLocaleString('de-AT', { dateStyle: 'short', timeStyle: 'short' })} ·{' '}
                {l.actor_name || l.actor_email} hat {l.target_name || l.target_email} den Zugriff{' '}
                <span className={l.action === 'granted' ? 'text-emerald-600 font-semibold' : 'text-rose-600 font-semibold'}>
                  {l.action === 'granted' ? 'erteilt' : 'entzogen'}
                </span>
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const KanzleiSecurityPanel = ({ onClose }) => {
  const [status, setStatus] = useState(null);
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [enroll, setEnroll] = useState(null); // {provisioning_uri, manual_key}
  const [enrollCode, setEnrollCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [disablePw, setDisablePw] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [busy, setBusy] = useState(false);

  const loadStatus = () => axios.get(`${API}/advisor/auth/2fa/status`).then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { loadStatus(); }, []);

  const err = (e, fallback) => toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : fallback);

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw !== newPw2) { toast.error('Die Passwörter stimmen nicht überein.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/advisor/auth/change-password`, { current_password: curPw, new_password: newPw });
      toast.success('Passwort geändert.');
      setCurPw(''); setNewPw(''); setNewPw2('');
      loadStatus();
    } catch (e2) { err(e2, 'Passwortänderung fehlgeschlagen.'); } finally { setBusy(false); }
  };

  const startEnroll = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/advisor/auth/2fa/enroll/start`);
      setEnroll(r.data);
    } catch (e2) { err(e2, '2FA-Einrichtung fehlgeschlagen.'); } finally { setBusy(false); }
  };

  const confirmEnroll = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/advisor/auth/2fa/enroll/confirm`, { code: enrollCode });
      setRecoveryCodes(r.data.recovery_codes);
      setEnroll(null); setEnrollCode('');
      toast.success('2FA aktiviert.');
      loadStatus();
    } catch (e2) { err(e2, 'Code ungültig.'); } finally { setBusy(false); }
  };

  const disable2fa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/advisor/auth/2fa/disable`, { password: disablePw, code: disableCode });
      toast.success('2FA deaktiviert.');
      setDisablePw(''); setDisableCode(''); setRecoveryCodes(null);
      loadStatus();
    } catch (e2) { err(e2, 'Deaktivierung fehlgeschlagen.'); } finally { setBusy(false); }
  };

  const copyRecovery = () => {
    navigator.clipboard?.writeText(recoveryCodes.join('\n'));
    toast.success('Recovery-Codes kopiert.');
  };

  const label = 'block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:p-8" data-testid="kanzlei-security-panel">
      <div className="w-full max-w-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4" style={{ background: '#0A1128' }}>
          <span className="lex-serif text-lg font-semibold text-white flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" style={{ color: '#C5A059' }} /> Sicherheit & Team
          </span>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="kanzlei-security-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* Team & Datenzugriff (Sep 2026) */}
          <TeamAccessSection />

          <hr className="border-slate-200" />

          {/* Passwort ändern */}
          <form onSubmit={changePassword} className="space-y-3">
            <h3 className="lex-serif text-base font-semibold flex items-center gap-2" style={{ color: '#0A1128' }}>
              <KeyRound className="h-4 w-4" /> Passwort ändern
            </h3>
            <div><label className={label}>Aktuelles Passwort</label>
              <input type="password" className="lex-input" value={curPw} onChange={(e) => setCurPw(e.target.value)} required data-testid="sec-current-password" /></div>
            <div><label className={label}>Neues Passwort (mind. 12 Zeichen)</label>
              <input type="password" className="lex-input" value={newPw} onChange={(e) => setNewPw(e.target.value)} minLength={12} required data-testid="sec-new-password" /></div>
            <div><label className={label}>Neues Passwort wiederholen</label>
              <input type="password" className="lex-input" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} minLength={12} required data-testid="sec-new-password2" /></div>
            <button type="submit" className="lex-btn" disabled={busy} data-testid="sec-change-password-submit">Passwort ändern</button>
          </form>

          <hr className="border-slate-200" />

          {/* 2FA */}
          <div className="space-y-3">
            <h3 className="lex-serif text-base font-semibold flex items-center gap-2" style={{ color: '#0A1128' }}>
              <ShieldCheck className="h-4 w-4" /> Zwei-Faktor-Authentifizierung (TOTP)
            </h3>

            {status?.totp_enabled ? (
              <div className="space-y-3">
                <p className="text-sm text-emerald-700 font-medium" data-testid="sec-2fa-enabled">
                  ✓ 2FA ist aktiv · {status.recovery_codes_left} Recovery-Codes übrig
                </p>
                <form onSubmit={disable2fa} className="space-y-3 border border-slate-200 p-4">
                  <p className="text-xs text-slate-500">Zum Deaktivieren Passwort UND gültigen Code eingeben:</p>
                  <input type="password" className="lex-input" placeholder="Passwort" value={disablePw}
                    onChange={(e) => setDisablePw(e.target.value)} required data-testid="sec-disable-password" />
                  <input type="text" inputMode="numeric" className="lex-input" placeholder="6-stelliger Code oder Recovery-Code" value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.trim())} required data-testid="sec-disable-code" />
                  <button type="submit" className="text-sm text-rose-700 font-semibold underline" disabled={busy} data-testid="sec-disable-submit">
                    2FA deaktivieren
                  </button>
                </form>
              </div>
            ) : enroll ? (
              <form onSubmit={confirmEnroll} className="space-y-4">
                <p className="text-sm text-slate-600">QR-Code mit Ihrer Authenticator-App scannen (z. B. Google Authenticator, Aegis):</p>
                <div className="flex justify-center bg-white p-3 border border-slate-200 w-fit mx-auto">
                  <QRCodeSVG value={enroll.provisioning_uri} size={180} data-testid="sec-2fa-qr" />
                </div>
                <p className="lex-mono text-[11px] text-slate-500 break-all text-center">Manueller Schlüssel: {enroll.manual_key}</p>
                <input type="text" inputMode="numeric" maxLength={6} autoComplete="one-time-code"
                  className="lex-input text-center tracking-[0.4em] text-lg" placeholder="123456"
                  value={enrollCode} onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, ''))} required data-testid="sec-enroll-code" />
                <button type="submit" className="lex-btn w-full" disabled={busy || enrollCode.length !== 6} data-testid="sec-enroll-confirm">
                  2FA aktivieren
                </button>
              </form>
            ) : (
              <button onClick={startEnroll} className="lex-btn" disabled={busy} data-testid="sec-enroll-start">
                2FA einrichten
              </button>
            )}

            {recoveryCodes && (
              <div className="border p-4 space-y-2" style={{ borderColor: '#C5A059', background: '#FDFAF3' }} data-testid="sec-recovery-codes">
                <p className="text-sm font-semibold" style={{ color: '#0A1128' }}>
                  Recovery-Codes — jetzt sicher speichern (werden nur einmal angezeigt!):
                </p>
                <pre className="lex-mono text-xs leading-relaxed whitespace-pre-wrap">{recoveryCodes.join('\n')}</pre>
                <button onClick={copyRecovery} className="text-xs font-semibold underline flex items-center gap-1" style={{ color: '#0A1128' }} data-testid="sec-recovery-copy">
                  <Copy className="h-3 w-3" /> Kopieren
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KanzleiSecurityPanel;
